import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useDrawerDirty } from "@shell/components/DrawerShell";
import { SelectCard } from "@shell/components/SelectCard";

import type { CreateQueueRequest, ProvisionModeUI } from "../../../types";

import { useQueueForm, type QueueFormState } from "./useQueueForm";

type QueueType = "rabbitmq" | "nats" | "kafka" | "pulsar";

const QUEUE_TYPES: { value: QueueType; label: string; description: string }[] =
  [
    {
      value: "rabbitmq",
      label: "RabbitMQ",
      description: "AMQP broker, flexible routing",
    },
    {
      value: "nats",
      label: "NATS",
      description: "Lightweight pub/sub, JetStream",
    },
    {
      value: "kafka",
      label: "Kafka",
      description: "Partitioned log, high throughput",
    },
    {
      value: "pulsar",
      label: "Pulsar",
      description: "Multi-tenant, geo-replication",
    },
  ];

export interface QueueFormHandle {
  submit: () => boolean;
  getState: () => QueueFormState;
}

export interface QueueFormProps {
  onSubmit: (req: CreateQueueRequest) => void;
  isSubmitting: boolean;
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      component="button"
      onClick={onChange}
      disabled={disabled}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        p: 1.25,
        border: "none",
        borderRadius: 1,
        bgcolor: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "left",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      {checked ? (
        <ToggleOnIcon color="primary" />
      ) : (
        <ToggleOffIcon color="disabled" />
      )}
    </Box>
  );
}

export const QueueForm = React.forwardRef<QueueFormHandle, QueueFormProps>(
  function QueueForm({ onSubmit, isSubmitting }, ref): React.ReactElement {
    const { setIsDirty } = useDrawerDirty();
    const form = useQueueForm();

    const handleSubmit = () => {
      if (!form.validate()) return false;
      onSubmit(form.buildRequest());
      return true;
    };

    React.useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      getState: () => form.state,
    }));

    return (
      <Stack spacing={2.5}>
        {/* Name */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Queue Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="my_queue"
            value={form.state.name}
            onChange={(e) => {
              form.setField("name", e.target.value);
              setIsDirty(true);
            }}
            onBlur={() => form.onBlur("name")}
            error={!!form.errors.name}
            helperText={form.errors.name}
            disabled={isSubmitting}
          />
        </Box>

        {/* Queue Type */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Queue Type
          </Typography>
          <Stack spacing={1}>
            {QUEUE_TYPES.map((qt) => (
              <SelectCard
                key={qt.value}
                label={qt.label}
                description={qt.description}
                selected={form.state.queueType === qt.value}
                onClick={() => {
                  form.setField("queueType", qt.value);
                  setIsDirty(true);
                }}
                disabled={isSubmitting}
              />
            ))}
          </Stack>
        </Box>

        {/* Mode */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Mode
          </Typography>
          <Stack spacing={1}>
            <SelectCard
              label="Shared"
              description={`Isolated namespace on shared ${form.state.queueType} cluster`}
              selected={form.state.mode === "shared"}
              onClick={() => {
                form.setField("mode", "shared" as ProvisionModeUI);
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
            <SelectCard
              label="Dedicated"
              description="Own cluster with dedicated resources"
              selected={form.state.mode === "dedicated"}
              onClick={() => {
                form.setField("mode", "dedicated" as ProvisionModeUI);
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
          </Stack>
        </Box>

        {/* Type-specific dedicated fields */}
        {form.state.mode === "dedicated" && (
          <Stack spacing={2.5} sx={{ animation: "fadeIn 200ms ease" }}>
            {form.state.queueType === "rabbitmq" && (
              <>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Replicas
                  </Typography>
                  <Stack spacing={1}>
                    <SelectCard
                      label="1 node"
                      description="Single broker (no HA)"
                      selected={form.state.replicas === 1}
                      onClick={() => {
                        form.setField("replicas", 1);
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                    <SelectCard
                      label="3 nodes"
                      description="Quorum queue HA"
                      selected={form.state.replicas === 3}
                      onClick={() => {
                        form.setField("replicas", 3);
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                  </Stack>
                </Box>
                <ToggleRow
                  label="Persistence"
                  description="Durable messages on disk"
                  checked={form.state.persistence}
                  onChange={() => {
                    form.setField("persistence", !form.state.persistence);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
                <ToggleRow
                  label="Management UI"
                  description="RabbitMQ management plugin"
                  checked={form.state.managementUi}
                  onChange={() => {
                    form.setField("managementUi", !form.state.managementUi);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
              </>
            )}

            {form.state.queueType === "nats" && (
              <ToggleRow
                label="JetStream"
                description="Persistent streaming and key-value"
                checked={form.state.jetStream}
                onChange={() => {
                  form.setField("jetStream", !form.state.jetStream);
                  setIsDirty(true);
                }}
                disabled={isSubmitting}
              />
            )}

            {form.state.queueType === "kafka" && (
              <>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Partitions
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={form.state.partitions}
                    onChange={(e) => {
                      form.setField("partitions", Number(e.target.value));
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting}
                    inputProps={{ min: 1, max: 100 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Replication Factor
                  </Typography>
                  <Stack spacing={1}>
                    <SelectCard
                      label="1"
                      description="No replication"
                      selected={form.state.replicationFactor === 1}
                      onClick={() => {
                        form.setField("replicationFactor", 1);
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                    <SelectCard
                      label="3"
                      description="3 replicas for HA"
                      selected={form.state.replicationFactor === 3}
                      onClick={() => {
                        form.setField("replicationFactor", 3);
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                  </Stack>
                </Box>
              </>
            )}

            {form.state.queueType === "pulsar" && (
              <Alert
                severity="info"
                sx={{ "& .MuiAlert-message": { fontSize: "0.875rem" } }}
              >
                Pulsar dedicated clusters are provisioned with default broker
                and bookie counts. Advanced configuration is available via API.
              </Alert>
            )}
          </Stack>
        )}

        {/* Advanced */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              Advanced
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <ToggleRow
                label="TLS"
                description="Encrypt connections"
                checked={form.state.tls}
                onChange={() => {
                  form.setField("tls", !form.state.tls);
                  setIsDirty(true);
                }}
                disabled={isSubmitting}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Authentication
                </Typography>
                <Stack spacing={0.5}>
                  {(["none", "token", "mtls"] as const).map((a) => (
                    <SelectCard
                      key={a}
                      label={
                        a === "mtls"
                          ? "mTLS"
                          : a.charAt(0).toUpperCase() + a.slice(1)
                      }
                      selected={form.state.auth === a}
                      onClick={() => {
                        form.setField("auth", a);
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </Stack>
    );
  },
);

QueueForm.displayName = "QueueForm";
